import { useEffect, useRef } from 'react';
import { getConsumer } from '../services/actionCableConsumer';
import { useShop } from '../contexts/ShopContext';
import {
  isPageForeground,
  notificationIdentity,
  shouldPlayNotificationSound,
  shouldDisplayInAppNotification,
} from '../utils/inAppNotifications';
import { showToast } from '../utils/toast';
import { emitRealtimeNotification } from '../utils/realtimeNotifications';
import { playNotificationSound } from '../utils/notificationSound';

const SHOP_LIFECYCLE_EVENTS = new Set([
  'pre_expiry_7_days',
  'pre_expiry_3_days',
  'grace_daily_reminder',
  'expired_final',
  'shop_inactivity_nudge',
  'shop_inactive',
]);

function getToastType(event) {
  switch (event) {
    case 'pre_expiry_7_days':
    case 'pre_expiry_3_days':
    case 'grace_daily_reminder':
    case 'expired_final':
    case 'shop_inactive':
      return 'warning';
    case 'shop_inactivity_nudge':
      return 'info';
    case 'seller_responded':
    case 'live_session_accepted':
    case 'order_paid':
    case 'order_delivered':
      return 'success';
    case 'live_session_rejected':
    case 'order_declined':
    case 'order_dispute':
      return 'warning';
    case 'buyer_request_broadcast':
    case 'live_session_requested':
    case 'live_session_ended':
    case 'order_created':
    case 'order_in_transit':
    case 'order_cancelled':
      return 'info';
    default:
      return 'info';
  }
}

function getMessage(payload) {
  if (payload?.event === 'message_thread_updated') {
    return payload?.data?.message?.body || 'You have a new message.';
  }
  return payload?.data?.message || 'You have a new notification.';
}

async function maybeShowBrowserNotification(payload) {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return;
  if (isPageForeground()) return;
  if (Notification.permission !== 'granted') return;

  const title = payload?.data?.title || payload?.notification?.title || 'YubuMarket';
  const body = getMessage(payload);
  const clickAction = payload?.data?.click_action;
  const tag = `${payload?.event || 'notification'}:${notificationIdentity(payload)}`;

  try {
    if (navigator.serviceWorker?.ready) {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        body,
        tag,
        icon: '/pwa-192x192.png',
        data: {
          ...(payload?.data || {}),
          click_action: clickAction,
        },
      });
      return;
    }
  } catch {
    // Fallback to page-level Notification below.
  }

  const notification = new Notification(title, { body, tag });
  if (clickAction) {
    notification.onclick = () => {
      window.focus();
      window.location.assign(clickAction);
      notification.close();
    };
  }
}

export default function RealtimeNotificationsBridge({ token }) {
  const seenRef = useRef(new Map());
  const { refreshShop } = useShop();

  useEffect(() => {
    if (!token) return undefined;

    const consumer = getConsumer(token);
    const subscription = consumer.subscriptions.create(
      { channel: 'NotificationsChannel' },
      {
        received(payload) {
          const event = payload?.event || payload?.type;
          if (!event) return;

          const key = `${event}:${notificationIdentity(payload)}`;
          const now = Date.now();
          const lastSeen = seenRef.current.get(key) || 0;
          if (now - lastSeen < 3000) return;
          seenRef.current.set(key, now);

          emitRealtimeNotification(payload);
          if (shouldPlayNotificationSound(payload)) {
            playNotificationSound();
          }
          if (shouldDisplayInAppNotification(payload)) {
            showToast(getToastType(event), getMessage(payload));
          } else {
            void maybeShowBrowserNotification(payload);
          }

          if (SHOP_LIFECYCLE_EVENTS.has(event) && typeof refreshShop === 'function') {
            Promise.resolve(refreshShop()).catch(() => null);
          }
        },
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [refreshShop, token]);

  return null;
}
